import React, { useState, useCallback } from 'react';
import './effects.css';
import Dashboard from './components/Dashboard';
import ControlPanel from './components/ControlPanel';
import Injector from './components/Injector';
import CombustionChamber from './components/CombustionChamber';
import PhaseControl from './components/PhaseControl';
import Valves from './components/Valves';
import { useEngineSimulation } from './hooks/useEngineSimulation';
import { useGoogleAnalytics } from './hooks/useGoogleAnalytics';

const SIMULATION_SPEEDS = [
    { label: '1/10x', value: 0.1 },
    { label: '1/50x', value: 0.02 },
    { label: '1/100x', value: 0.01 },
];

const StatusChip: React.FC<{ active: boolean; activeClass: string; children: React.ReactNode }> = ({ active, activeClass, children }) => (
  <li className={`px-3 py-1 rounded-full border transition-colors ${active ? activeClass : 'border-slate-700 text-slate-600'}`}>
    {children}
  </li>
);

const App: React.FC = () => {
  // Inizializza Google Analytics
  useGoogleAnalytics();

  const [rpm, setRpm] = useState<number>(800);
  const [pressure, setPressure] = useState<number>(1200);
  const [openingTime, setOpeningTime] = useState<number>(1.5);
  const [temperature, setTemperature] = useState<number>(1200);
  const [isRunning, setIsRunning] = useState<boolean>(false);
  const [isFlameViewActive, setIsFlameViewActive] = useState<boolean>(false);
  const [simulationSpeed, setSimulationSpeed] = useState<number>(0.01); // Default 1/100x: un ciclo in circa 7 s a 800 RPM

  const toggleSimulation = useCallback(() => {
    setIsRunning(prev => !prev);
  }, []);

  const toggleFlameView = useCallback(() => {
    setIsFlameViewActive(prev => {
        const nextState = !prev;
        if (nextState) {
            setIsRunning(false);
        }
        return nextState;
    });
  }, []);

  const {
    enginePhase,
    setEnginePhase,
    effectivePhase,
    isInjecting,
    isCombusting,
    intakeOpen,
    exhaustOpen,
  } = useEngineSimulation({
    rpm,
    openingTime,
    isRunning,
    isFlameViewActive,
    simulationSpeed,
  });


  return (
    <main className="min-h-screen bg-slate-900 flex flex-col items-center p-4 lg:p-8 font-sans">
      <header className="text-center w-full mb-8">
        <a href="/" className="inline-block mb-4 text-sm text-slate-400 hover:text-cyan-400 transition-colors">
          ← Torna al portfolio
        </a>
        <h1 className="text-4xl md:text-5xl font-extrabold text-white">
          Simulatore <span className="text-cyan-400">Iniettore Diesel</span>
        </h1>
        <p className="text-slate-400 mt-2">Usa i controlli per avviare il motore o per analizzare il ciclo manualmente.</p>
      </header>

      <div className="w-full max-w-7xl flex-grow grid grid-cols-1 lg:grid-cols-5 gap-8 items-center">
        {/* Colonna Sinistra e Centrale: Simulazione e Controllo Fase */}
        <section aria-labelledby="simulation-title" className="lg:col-span-3 w-full flex flex-col md:flex-row items-center justify-center gap-8 bg-slate-900/50 rounded-lg border border-slate-800 p-4 shadow-2xl">
            <h2 id="simulation-title" className="sr-only">Simulazione del cilindro</h2>
            <div className="flex flex-col items-center">
              {/* Badge con spazio sempre riservato, FUORI dal blocco su cui sono ancorate le valvole:
                  non deve spostare iniettore e cilindro quando il motore si accende. */}
              <div className="mb-3 h-7 flex items-center">
                {!isFlameViewActive && isRunning && (
                    <p className="bg-green-500 text-white font-bold px-3 py-1 rounded-md shadow-lg animate-pulse text-sm" role="status">
                      MOTORE ACCESO
                    </p>
                )}
              </div>
              {/* Le valvole sono posizionate in assoluto rispetto a questo blocco (iniettore + camera) */}
              <div className="relative flex flex-col items-center">
                <Injector isInjecting={isInjecting} />
                { !isFlameViewActive && <Valves intakeOpen={intakeOpen} exhaustOpen={exhaustOpen} isRunning={isRunning} /> }
                <div className="-mt-4">
                  <CombustionChamber
                    isRunning={isRunning}
                    isInjecting={isInjecting}
                    isCombusting={isCombusting}
                    pressure={pressure}
                    isFlameViewActive={isFlameViewActive}
                    temperature={temperature}
                    enginePhase={effectivePhase} // Passa la fase sfasata per la logica interna (es. pistone)
                  />
                </div>
              </div>
              {!isFlameViewActive && (
                <ul className="mt-4 flex flex-wrap justify-center gap-2 text-xs font-semibold" aria-label="Stato del ciclo">
                  <StatusChip active={isInjecting} activeClass="bg-cyan-500/20 text-cyan-300 border-cyan-500/50">Iniezione</StatusChip>
                  <StatusChip active={isCombusting} activeClass="bg-orange-500/20 text-orange-300 border-orange-500/50">Combustione</StatusChip>
                  <StatusChip active={intakeOpen} activeClass="bg-sky-500/20 text-sky-300 border-sky-500/50">Entrata aperta</StatusChip>
                  <StatusChip active={exhaustOpen} activeClass="bg-slate-400/20 text-slate-200 border-slate-400/50">Scarico aperto</StatusChip>
                </ul>
              )}
            </div>
            <div className="flex-shrink-0">
              <PhaseControl
                phase={enginePhase}
                setPhase={setEnginePhase}
                isDisabled={isRunning || isFlameViewActive}
              />
            </div>
        </section>

        {/* Colonna Destra: Controlli e Dati */}
        <section aria-labelledby="controls-title" className="lg:col-span-2 flex flex-col space-y-8">
           <h2 id="controls-title" className="sr-only">Controlli e Dati</h2>
          <Dashboard rpm={rpm} pressure={pressure} openingTime={openingTime} temperature={temperature} phase={enginePhase} />
          <ControlPanel
            rpm={rpm} setRpm={setRpm}
            pressure={pressure} setPressure={setPressure}
            openingTime={openingTime} setOpeningTime={setOpeningTime}
            isRunning={isRunning}
            toggleSimulation={toggleSimulation}
            isFlameViewActive={isFlameViewActive}
            toggleFlameView={toggleFlameView}
            temperature={temperature}
            setTemperature={setTemperature}
            simulationSpeeds={SIMULATION_SPEEDS}
            simulationSpeed={simulationSpeed}
            setSimulationSpeed={setSimulationSpeed}
          />
        </section>
      </div>

      <footer className="text-center text-slate-500 text-sm py-4 mt-8">
        <p>Creato con React, TypeScript, e Tailwind CSS. Visualizzazione solo a scopo dimostrativo.</p>
      </footer>
    </main>
  );
};

export default App;
